import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import startOfYesterday from 'date-fns/startOfYesterday';
import isBefore from 'date-fns/isBefore';
import { GameMode } from '../../lib/gameMode';
import { getTodaysPuzzleIndex } from '../../lib/utils';

export type ModeStreakStats = {
  maxStreak: number;
  streak: number;
  lastDailyWin?: number;
};

export type StatsState = Record<GameMode, ModeStreakStats>;

export type StreakPayload = {
  mode: GameMode;
  puzzleIndex: number;
};

export const emptyModeStats = (): ModeStreakStats => ({
  maxStreak: 0,
  streak: 0,
  lastDailyWin: undefined,
});

const initialState: StatsState = {
  classic: emptyModeStats(),
  large: emptyModeStats(),
};

export const normalizeStatsState = (raw: unknown): StatsState => {
  const empty = {
    classic: emptyModeStats(),
    large: emptyModeStats(),
  };
  if (!raw || typeof raw !== 'object') return empty;

  const prev = raw as StatsState & ModeStreakStats;
  if (prev.classic && typeof prev.classic === 'object') {
    return {
      classic: {
        streak: Number(prev.classic.streak) || 0,
        maxStreak: Number(prev.classic.maxStreak) || 0,
        lastDailyWin: prev.classic.lastDailyWin,
      },
      large: {
        streak: Number(prev.large?.streak) || 0,
        maxStreak: Number(prev.large?.maxStreak) || 0,
        lastDailyWin: prev.large?.lastDailyWin,
      },
    };
  }

  return {
    classic: {
      streak: Number(prev.streak) || 0,
      maxStreak: Number(prev.maxStreak) || 0,
      lastDailyWin: prev.lastDailyWin,
    },
    large: emptyModeStats(),
  };
};

const ensureModeStats = (state: StatsState, mode: GameMode): ModeStreakStats => {
  if (!state[mode] || typeof state[mode] !== 'object') state[mode] = emptyModeStats();
  return state[mode];
};

export const statsSlice = createSlice({
  name: 'stats',
  initialState,
  reducers: {
    updateStreakWithWin: (state, action: PayloadAction<StreakPayload>) => {
      const { mode, puzzleIndex } = action.payload;
      if (puzzleIndex !== getTodaysPuzzleIndex(mode)) return;

      const modeStats = ensureModeStats(state, mode);

      if (!modeStats.lastDailyWin || isBefore(modeStats.lastDailyWin, startOfYesterday())) {
        modeStats.streak = 1;
      } else {
        modeStats.streak += 1;
      }

      if (modeStats.streak > modeStats.maxStreak) modeStats.maxStreak = modeStats.streak;
      modeStats.lastDailyWin = +new Date();
    },
    updateStreakWithLoss: (state, action: PayloadAction<StreakPayload>) => {
      const { mode, puzzleIndex } = action.payload;
      if (puzzleIndex !== getTodaysPuzzleIndex(mode)) return;

      const modeStats = ensureModeStats(state, mode);
      modeStats.streak = 0;
      modeStats.lastDailyWin = undefined;
    },
  },
});

export const { updateStreakWithWin, updateStreakWithLoss } = statsSlice.actions;

export default statsSlice.reducer;
